import * as React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, PieLabelRenderProps } from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { useEffect, useState } from "react";
import { Select } from "antd";
import { getDadosAlunos } from "@/services/aluno";

const COLORS = ["#6366f1", "#06b6d4", "#f59e42", "#ef4444", "#a21caf", "#f43f5e", "#22d3ee", "#facc15"];

type PieData = { name: string; value: number };

function renderCustomizedLabel(props: PieLabelRenderProps) {
  const {
    cx = 0,
    cy = 0,
    midAngle = 0,
    innerRadius = 0,
    outerRadius = 0,
    value = 0,
    index = 0,
  } = props;
  const cxNum = typeof cx === 'number' ? cx : Number(cx);
  const cyNum = typeof cy === 'number' ? cy : Number(cy);
  const midAngleNum = typeof midAngle === 'number' ? midAngle : Number(midAngle);
  const innerRadiusNum = typeof innerRadius === 'number' ? innerRadius : Number(innerRadius);
  const outerRadiusNum = typeof outerRadius === 'number' ? outerRadius : Number(outerRadius);
  const RADIAN = Math.PI / 180;
  const radius = innerRadiusNum + (outerRadiusNum - innerRadiusNum) * 0.6;
  const x = cxNum + radius * Math.cos(-midAngleNum * RADIAN);
  const y = cyNum + radius * Math.sin(-midAngleNum * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor={x > cxNum ? "start" : "end"} dominantBaseline="central" fontSize={13} fontWeight={600}>
      {value}
    </text>
  );
}

function ChartLegend({ data, colors }: { data: PieData[]; colors: string[] }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (!data.length) return null;
  return (
    <ul
      className="overflow-y-auto space-y-1 pr-0.5"
      style={{
        maxHeight: '7.5rem',
        scrollbarWidth: 'thin',
        scrollbarColor: '#334155 transparent',
      } as React.CSSProperties}
    >
      {data.map((entry, idx) => {
        const percent = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
        return (
          <li
            key={entry.name + idx}
            className="flex items-center gap-2 text-xs text-gray-300 py-1 px-2 rounded-md hover:bg-slate-800/60 transition-colors cursor-default"
          >
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ background: colors[idx % colors.length] }}
            />
            <span className="truncate flex-1 min-w-0">{entry.name}</span>
            <span className="flex-shrink-0 font-semibold text-white tabular-nums">{entry.value}</span>
            <span className="flex-shrink-0 text-gray-500 tabular-nums w-[3.5rem] text-right">({percent}%)</span>
          </li>
        );
      })}
    </ul>
  );
}

export default function Dashboard() {
  const [totalAlunos, setTotalAlunos] = useState(0);
  const [preMatriculados, setPreMatriculados] = useState(0);
  const [matriculasMes, setMatriculasMes] = useState(0);
  const [orcamentos, setOrcamentos] = useState(0);
  const [dataRegiao, setDataRegiao] = useState<PieData[]>([]);
  const [dataEscola, setDataEscola] = useState<PieData[]>([]);
  const [dataIndicacao, setDataIndicacao] = useState<PieData[]>([]);
  const [dataTurma, setDataTurma] = useState<PieData[]>([]);
  const [dataCursoPretendido, setDataCursoPretendido] = useState<PieData[]>([]);
  const [turmas, setTurmas] = useState<{ id: number; nome: string }[]>([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState<string | null>(null);
  const [dataEspecifica, setDataEspecifica] = useState<PieData[]>([]);
  const [dataServico, setDataServico] = useState<PieData[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(false);

  let unidadeLogada = "";
  try {
    const unidade = JSON.parse(localStorage.getItem("UnidadeLogada") as any);
    unidadeLogada = unidade?.Nome || unidade?.nome || "";
  } catch {
    unidadeLogada = "";
  }

  useEffect(() => {
    let alunosData: any;
    async function fetchDadosAlunos() {
      try {
        const response = await getDadosAlunos();
        if (response.status === 200 && Array.isArray(response.data)) {
          alunosData = response.data;
          localStorage.setItem("AlunosData", JSON.stringify(alunosData));
        } else {
          alunosData = [];
        }
      } catch {
        alunosData = [];
      }

      const turmasUnicas = Array.from(
        new Map<number, { id: number; nome: string }>(
          alunosData.map((aluno: any) => [aluno.turma.id, aluno.turma])
        ).values()
      );
      setTurmas(turmasUnicas);
      if (turmasUnicas.length > 0) {
        setSelectedTurmaId(String(turmasUnicas[0].id));
      }
      processDadosAlunos(alunosData || []);
    }
    fetchDadosAlunos();
  }, [unidadeLogada]);

  const processDadosAlunos = (alunosData: any[]) => {
    // Ajuste o filtro conforme o campo de ativo na sua base
    const ativos = alunosData.filter((aluno: any) => aluno.status.id === 1 && aluno.unidade.nome === unidadeLogada);
    setTotalAlunos(ativos.length);
    // Pré-matriculados: status.Id === 4 e unidade.id igual à unidade selecionada
    const preMatriculados = alunosData.filter(
      (aluno: any) => aluno.status.id === 4 && aluno.unidade.nome === unidadeLogada
    );
    setPreMatriculados(preMatriculados.length);
    // Matrículas no mês: alunos com dataEmissaoContrato no mês atual
    const now = new Date();
    const matriculasNoMes = alunosData.filter((aluno: any) => {
      if (!aluno.dataEmissaoContrato) return false;
      const data = new Date(aluno.dataEmissaoContrato);
      return data.getMonth() === now.getMonth() && data.getFullYear() === now.getFullYear();
    });
    setMatriculasMes(matriculasNoMes.length);

    // Orcamentos: status.Id === 5 e unidade.id igual à unidade selecionada
    const orcamentos = alunosData.filter(
      (aluno: any) => aluno.status.id === 5 && aluno.unidade.nome === unidadeLogada
    );
    setOrcamentos(orcamentos.length);

    // Agrupar por Bairro
    const regiaoMap = new Map();
    alunosData.filter((item: any) => item.status.id === 4 || item.status.id === 1).forEach((item: any) => {
      const key = item.bairro || 'Não informado';
      regiaoMap.set(key, (regiaoMap.get(key) || 0) + 1);
    });
    const dataRegiaoArray = Array.from(regiaoMap, ([name, value]) => ({ name, value }));
    setDataRegiao(dataRegiaoArray.sort((a, b) => b.value - a.value));

    // Agrupar por EscolaEnsinoMedio
    const escolaMap = new Map();
    alunosData.filter((item: any) => item.status.id === 4 || item.status.id === 1).forEach((item: any) => {
      const key = item.escolaEnsinoMedio?.nome || 'Não Aluno EM';
      escolaMap.set(key, (escolaMap.get(key) || 0) + 1);
    });
    const dataEscolaArray = Array.from(escolaMap, ([name, value]) => ({ name, value }));
    setDataEscola(dataEscolaArray.sort((a, b) => b.value - a.value));

    // Agrupar por TipoIndicacao
    const indicacaoMap = new Map();
    alunosData.filter((item: any) => item.status.id === 4 || item.status.id === 1).forEach((item: any) => {
      const key = item.tipoIndicacao?.nome || 'Não informado';
      indicacaoMap.set(key, (indicacaoMap.get(key) || 0) + 1);
    });
    const dataIndicacaoArray = Array.from(indicacaoMap, ([name, value]) => ({ name, value }));
    setDataIndicacao(dataIndicacaoArray.sort((a, b) => b.value - a.value));

    // Agrupar por Turma
    const turmaMap = new Map();
    alunosData.filter((item: any) => item.status.id === 4 || item.status.id === 1).forEach((item: any) => {
      const key = item.turma?.nome || 'Não informado';
      turmaMap.set(key, (turmaMap.get(key) || 0) + 1);
    });
    const dataTurmaArray = Array.from(turmaMap, ([name, value]) => ({ name, value }));
    setDataTurma(dataTurmaArray.sort((a, b) => b.value - a.value));

    // Agrupar por Curso Pretendido
    const cursoPretendidoMap = new Map();
    alunosData.filter((item: any) => item.status.id === 4 || item.status.id === 1).forEach((item: any) => {
      const key = item.cursoPretendido || 'Não informado';
      cursoPretendidoMap.set(key, (cursoPretendidoMap.get(key) || 0) + 1);
    });
    const dataCursoPretendidoArray = Array.from(cursoPretendidoMap, ([name, value]) => ({ name, value }));
    setDataCursoPretendido(dataCursoPretendidoArray.sort((a, b) => b.value - a.value));
  }

  useEffect(() => {
    if (!selectedTurmaId) {
      setDataEspecifica([]);
      setDataServico([]);
      return;
    } 
    setLoadingCharts(true);
    const alunosData = localStorage.getItem("AlunosData");
    if (alunosData) {
      const alunos = JSON.parse(alunosData);
      const filtered = alunos.filter((aluno: any) => String(aluno.turma?.id) === selectedTurmaId && (aluno.status.id === 1 || aluno.status.id === 4));

      const mapEsp = new Map<string, number>();
      filtered.forEach((item: any) => {
        (item.especificas || []).forEach((especifica: any) => {
          const key = especifica.especifica?.nome || 'Não informado';
          mapEsp.set(key, (mapEsp.get(key) || 0) + 1);
        });
      });
      const dataEspecificaArray = Array.from(mapEsp, ([name, value]) => ({ name, value }));
      setDataEspecifica(dataEspecificaArray.sort((a, b) => b.value - a.value));

      const mapServ = new Map<string, number>();
      filtered.forEach((item: any) => {
        (item.servicos || []).forEach((servico: any) => {
          const key = servico.servico?.nome || 'Não informado';
          mapServ.set(key, (mapServ.get(key) || 0) + 1);
        });
      });
      const dataServicoArray = Array.from(mapServ, ([name, value]) => ({ name, value }));
      setDataServico(dataServicoArray.sort((a, b) => b.value - a.value));

      setLoadingCharts(false);
    }
  }, [selectedTurmaId]);

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-2 md:px-8 py-8 max-w-7xl mx-auto">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10 w-full">
        <div className="rounded-xl shadow-lg bg-gradient-to-r from-indigo-500 to-blue-500 p-6 flex flex-col items-center w-full flex-1">
          <span className="text-3xl font-bold text-white">{totalAlunos}</span>
          <span className="text-white text-sm mt-2">Alunos Matriculados</span>
        </div>
        <div className="rounded-xl shadow-lg bg-gradient-to-r from-pink-500 to-rose-400 p-6 flex flex-col items-center w-full flex-1">
          <span className="text-3xl font-bold text-white">{preMatriculados}</span>
          <span className="text-white text-sm mt-2">Pré-Matriculados</span>
        </div>
        <div className="rounded-xl shadow-lg bg-gradient-to-r from-green-400 to-teal-400 p-6 flex flex-col items-center w-full flex-1">
          <span className="text-3xl font-bold text-white">{orcamentos}</span>
          <span className="text-white text-sm mt-2">Orçamentos</span>
        </div>
      </div>
      {/* Gráficos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">


        {/* Card combinado: Específica e Serviço lado a lado */}
        <Card className="col-span-1 md:col-span-2 bg-gray-950/90 rounded-2xl shadow-xl flex flex-col p-0 overflow-hidden">
          <CardHeader className="w-full text-center px-4 pt-4 pb-2">
            <CardTitle className="text-lg md:text-xl font-bold text-indigo-300">Alunos por Específica e Serviço</CardTitle>
            <CardDescription className="text-gray-400">Selecione uma turma para visualizar a distribuição por específica e por serviço</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col px-4 pb-4 gap-4">
            <div className="flex justify-center">
              <Select
                placeholder="Selecione uma turma"
                style={{ width: 320 }}
                value={selectedTurmaId}
                onChange={(value: string) => setSelectedTurmaId(value)}
                allowClear
                onClear={() => setSelectedTurmaId(null)}
                options={turmas.map((t) => ({ value: String(t.id), label: t.nome }))}
              />
            </div>

            {selectedTurmaId && (
              loadingCharts ? (
                <div className="text-center text-gray-400 py-8">Carregando...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Específica */}
                  <div className="flex flex-col gap-3 md:border-r md:border-slate-700/50 md:pr-6">
                    <p className="text-center text-sm font-semibold text-indigo-300 tracking-wide">Específica</p>
                    {dataEspecifica.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">Nenhum dado encontrado.</div>
                    ) : (
                      <>
                        <div className="h-48 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={dataEspecifica}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                innerRadius={32}
                                labelLine={false}
                                label={renderCustomizedLabel}
                                isAnimationActive={true}
                              >
                                {dataEspecifica.map((entry, index) => (
                                  <Cell key={`cell-especifica-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => [`${value} alunos`, '']}
                                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                                itemStyle={{ color: '#e2e8f0' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="border-t border-slate-700/50" />
                        <ChartLegend data={dataEspecifica} colors={COLORS} />
                      </>
                    )}
                  </div>

                  {/* Serviço */}
                  <div className="flex flex-col gap-3">
                    <p className="text-center text-sm font-semibold text-indigo-300 tracking-wide">Serviço</p>
                    {dataServico.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">Nenhum dado encontrado.</div>
                    ) : (
                      <>
                        <div className="h-48 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={dataServico}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                innerRadius={32}
                                labelLine={false}
                                label={renderCustomizedLabel}
                                isAnimationActive={true}
                              >
                                {dataServico.map((entry, index) => (
                                  <Cell key={`cell-servico-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => [`${value} alunos`, '']}
                                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                                itemStyle={{ color: '#e2e8f0' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="border-t border-slate-700/50" />
                        <ChartLegend data={dataServico} colors={COLORS} />
                      </>
                    )}
                  </div>
                </div>
              )
            )}
          </CardContent>
        </Card>

        <Card className="bg-gray-950/90 rounded-2xl shadow-xl flex flex-col p-0 overflow-hidden">
          <CardHeader className="w-full text-center px-4 pt-4 pb-2">
            <CardTitle className="text-lg md:text-xl font-bold text-indigo-300">Matrículas por Turma</CardTitle>
            <CardDescription className="text-gray-400">Quantidade de alunos por turma</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col px-4 pb-4 gap-3">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dataTurma}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={32}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    isAnimationActive={true}
                  >
                    {dataTurma.map((entry, index) => (
                      <Cell key={`cell-turma-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} matrículas`, '']}
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t border-slate-700/50" />
            <ChartLegend data={dataTurma} colors={COLORS} />
          </CardContent>
        </Card>

        <Card className="bg-gray-950/90 rounded-2xl shadow-xl flex flex-col p-0 overflow-hidden">
          <CardHeader className="w-full text-center px-4 pt-4 pb-2">
            <CardTitle className="text-lg md:text-xl font-bold text-indigo-300">Matrículas por Região</CardTitle>
            <CardDescription className="text-gray-400">Alunos matriculados por bairro</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col px-4 pb-4 gap-3">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dataRegiao}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={32}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    isAnimationActive={true}
                  >
                    {dataRegiao.map((entry, index) => (
                      <Cell key={`cell-regiao-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} matrículas`, '']}
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t border-slate-700/50" />
            <ChartLegend data={dataRegiao} colors={COLORS} />
          </CardContent>
        </Card>

        <Card className="bg-gray-950/90 rounded-2xl shadow-xl flex flex-col p-0 overflow-hidden">
          <CardHeader className="w-full text-center px-4 pt-4 pb-2">
            <CardTitle className="text-lg md:text-xl font-bold text-indigo-300">Matrículas por Escola</CardTitle>
            <CardDescription className="text-gray-400">Alunos matriculados por escola</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col px-4 pb-4 gap-3">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dataEscola}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={32}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    isAnimationActive={true}
                  >
                    {dataEscola.map((entry, index) => (
                      <Cell key={`cell-escola-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} matrículas`, '']}
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t border-slate-700/50" />
            <ChartLegend data={dataEscola} colors={COLORS} />
          </CardContent>
        </Card>

        <Card className="bg-gray-950/90 rounded-2xl shadow-xl flex flex-col p-0 overflow-hidden">
          <CardHeader className="w-full text-center px-4 pt-4 pb-2">
            <CardTitle className="text-lg md:text-xl font-bold text-indigo-300">Curso Pretendido</CardTitle>
            <CardDescription className="text-gray-400">Quantidade de alunos por curso</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col px-4 pb-4 gap-3">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dataCursoPretendido}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={32}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    isAnimationActive={true}
                  >
                    {dataCursoPretendido.map((entry, index) => (
                      <Cell key={`cell-curso-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} matrículas`, '']}
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t border-slate-700/50" />
            <ChartLegend data={dataCursoPretendido} colors={COLORS} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
